import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    Button,
    CircularProgress,
    FormLabel,
    OptionType,
    SearchableSelect,
    serverError
} from "shared";
import { ITemplate, ITemplateComponent, ITemplateProcureComponent } from "entities/template";
import { authContext, propertiesContext, usePermission } from "entities/user";
import { CdocFormat, constructDocumentPayload, documentService } from "entities/document";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { buildFieldVisibilityContext } from "../../../../lib/utils/buildFieldVisibilityContext";
import { parseTicketResponse } from "../../../../lib/utils/ticket_integration";
import { constructOptions } from "../../../../lib/utils/dynamic_document_form_utils";
import { setLocalStorageFormRecord } from "../../../../lib/utils/record_localstorage";
import { scrollToErrorFields } from "../../../../lib/utils/scrollToError";
import { IPROC_SUBTYPES, IPROC_STYLES, IPROC_SUBTYPE_PROP } from "../../../../lib/utils/iproc_integration";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_procure.module.scss";

interface IDocumentFormProcureProps {
    component: ITemplateProcureComponent;
    template: ITemplate;
    templateComponents: ITemplateComponent[];
    onValidationCheck: () => Promise<boolean>;
    formRecord: Record<string, any>;
    label?: string;
    tooltip: IComponentTooltipSettings;
    enLanguageEnabled: boolean;

}

export const DocumentFormProcure: React.FC<IDocumentFormProcureProps> = React.memo(({
                                                                                        component,
                                                                                        label,
                                                                                        tooltip,
                                                                                        template,
                                                                                        templateComponents,
                                                                                        enLanguageEnabled,
                                                                                        onValidationCheck,
                                                                                        formRecord,
                                                                                    }) => {
    const t = useComponentsTranslations();
    const { i18n } = useTranslation();
    const [requestStyle, setRequestStyle] = useState<OptionType>();
    const [procSubType, setProcSubtype] = useState<OptionType>();
    const [triggerValidate, setTriggerValidate] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const { getProperty } = useContext(propertiesContext);
    const userInfo = useContext(authContext);
    const canSeeYandexProcureForm = usePermission("canSeeYandexProcureForm");
    const iprocSubtypes = constructOptions(IPROC_SUBTYPES);

    const isExternal = useMemo(() => {
        return !!userInfo && userInfo?.isExternal === true;
    }, [userInfo]);

    const fieldVisibilityContext = useMemo(
        () => buildFieldVisibilityContext(
            formRecord,
            templateComponents,
            template,
            enLanguageEnabled,
            i18n.language,
            isExternal,
        ),
        [formRecord, templateComponents, template, enLanguageEnabled, i18n.language, isExternal],
    );

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    useEffect(() => {
        setTimeout(() => {
            if (component.defaultValue) {
                setRequestStyle({
                    value: component.defaultValue,
                    label: component.defaultValue
                });

                if (component.defaultValue === IPROC_SUBTYPE_PROP && component.additionalDefaultValue) {
                    setProcSubtype({
                        value: component.additionalDefaultValue,
                        label: component.additionalDefaultValue
                    });
                }
            }
        });
    }, [component.defaultValue, component.additionalDefaultValue]);

    const updatedFormRecord = useMemo(() => {
        const updtRecord: Record<string, any> = {
            ...formRecord,
            xxya_cdoc_iproc: "Y",
            startrekQueueIproc: component.startrekQueueIproc,
            isTemplate: component.isTemplate
        };

        if (requestStyle) {
            updtRecord.iprocStyle = requestStyle.value;
        }

        if (procSubType) {
            updtRecord.iprocSubtype = procSubType.value;
        }

        return updtRecord;
    }, [formRecord, component, requestStyle, procSubType]);

    const purchaseOptions = useMemo(() => {
        const list = component.list && component.list.length > 0 ? component.list : IPROC_STYLES.map(s => s.name);
        const options = list?.map(opt => {
            return {
                value: opt,
                label: opt
            };
        });
        return options ? options : [];
    }, [component.list, i18n.language, enLanguageEnabled]);

    const handleClearEvent = () => {
        setRequestStyle(undefined);
        setProcSubtype(undefined);
    };

    const importToProcure = () => {
        const pdfPayload = constructDocumentPayload(template, updatedFormRecord, component.format === CdocFormat.rtf ? CdocFormat.rtf : CdocFormat.pdf, false, i18n.language, undefined, undefined, undefined, templateComponents, fieldVisibilityContext);
        const isRequestStyleValid = !!requestStyle?.value;

        onValidationCheck().then((isValid) => {
            setTriggerValidate((state) => state + 1);
            if (isValid && isRequestStyleValid) {
                setLoading(true);
                setLocalStorageFormRecord(template.id, formRecord);
                documentService.createTicket(pdfPayload).then(response => {
                    const parsedResponse = parseTicketResponse(response, t, isExternal, getProperty("startrekUrl"), t("createTicketError"));
                    if (parsedResponse.error) {
                        toast.error(parsedResponse.message);
                    } else {
                        toast.success(parsedResponse.message);
                    }
                }).catch(e => {
                    console.error(e);
                    toast.error(serverError(t("serverError"), e));
                }).finally(() => {
                    setLoading(false);
                });
            } else {
                scrollToErrorFields();
                toast.error(t("correctErrorsInFields"));
            }
        });
    };

    return canSeeYandexProcureForm ? <div className={styles.componentContainer}>
        <div className={styles.titleContainer}>
            <div className={styles.title}>{label}</div>
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltipContainer}/>
        </div>
        <div className={styles.container}>
            {!component.hideIprocStyle && <FormLabel title={t("purchaseRequestStyle")}>
                <SearchableSelect
                    options={purchaseOptions}
                    optionValue={requestStyle}
                    validate={true}
                    triggerValidate={triggerValidate}
                    onOptionChange={(val) => {
                        setRequestStyle((val ? val as OptionType : undefined));
                        setProcSubtype(undefined);
                    }}
                    isMulti={false}
                    hideClearIcon={false}
                    isSearchable={true}
                />
            </FormLabel>}
            {!component.hideIprocStyle && requestStyle?.value === IPROC_SUBTYPE_PROP &&
                <FormLabel title={t("purchaseRequestStyleSubtype")}>
                    <SearchableSelect
                        options={iprocSubtypes}
                        optionValue={procSubType}
                        onOptionChange={(val) => {
                            setProcSubtype((val ? val as OptionType : undefined));
                        }}
                        isMulti={false}
                        hideClearIcon={false}
                        isSearchable={true}
                    />
                </FormLabel>}
            <div className={styles.buttonGroup}>
                <Button className={styles.button}
                        view="legal"
                        disabled={loading}
                        onClick={importToProcure}>
                    <div className={styles.buttonInnerContainer}>
                        {t("importToYandexProcure")}
                        {loading && <CircularProgress size={15} className={styles.loader}/>}
                    </div>
                </Button>
            </div>
        </div>
    </div> : <div>{t("noInitiatorRole")}</div>;
});
